import type { EmployeeGrowth } from '@/types/career-growth';

interface GrowthContentInput {
    growth: EmployeeGrowth;
    /** Whether a competency profile is assigned; it alone fills the "Prochaine cible" card. */
    hasCompetencyProfile: boolean;
    /** Whether the reader may see the career journey notes; they are hidden from them otherwise. */
    canReadNotes: boolean;
}

/**
 * Whether the "Croissance" tab has anything at all to show. Anything is enough: one career step,
 * one recorded aspiration, one career meeting still waiting for its answers, one journey note the
 * reader may see, or an assigned competency profile. Without a single one of them the tab has no
 * content to arrange and gives its whole surface to the invitation instead.
 *
 * `nextLevelSkills` is deliberately absent: the API derives it from the target career step, so it
 * can never be filled when `careerSteps` is empty.
 *
 * The performance score is deliberately absent too, and for a different reason: it is measured on
 * the Évaluation tab and shown there as well, so a tab where nothing of the employee's career has
 * been recorded still reads as a beginning. It is a product decision, not an oversight -- flipping
 * it would put the dial back in front of the invitation.
 *
 * A note the reader may not see still counts as nothing *for them*: the section that would carry it
 * is not rendered either, so the tab really is empty on their screen.
 */
export const hasGrowthContent = ({
    growth,
    hasCompetencyProfile,
    canReadNotes,
}: GrowthContentInput): boolean =>
    growth.careerSteps.length > 0 ||
    growth.currentAspiration !== null ||
    growth.aspirationHistory.length > 0 ||
    hasCompetencyProfile ||
    (canReadNotes && growth.notes.totalCount > 0);
